<template>
  <div class="flex min-h-full flex-1 flex-col px-6 pt-[15vh] sm:px-6 lg:px-8">
    <template v-if="isNative && !isKeyboardOpen">
      <!-- Not gated on the SSO check: it's the only way off a server that doesn't answer. -->
      <div class="fixed right-0 bottom-12 left-0 flex justify-center">
        <router-link
          to="/connect"
          class="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          {{ t("msg.serverUrlChangeServer") }}
        </router-link>
      </div>

      <div v-if="bundleId" class="fixed right-0 bottom-4 left-0 flex justify-center">
        <span class="text-[10px] text-muted-foreground/50">Version {{ bundleId }}</span>
      </div>
    </template>

    <div class="no-focus-outline mx-auto w-full max-w-sm sm:max-w-md" ref="logoDiv" tabindex="-1">
      <WordmarkLogo class="mx-auto" />
    </div>

    <div class="mx-auto mt-6 flex w-full max-w-sm flex-col gap-6">
      <RateLimitCountdown v-if="rateLimitSeconds > 0" :seconds="rateLimitSeconds" @expired="rateLimitSeconds = 0" />

      <p v-if="errorMessage && !isRateLimited" class="text-danger">{{ t("msg.error") }}: {{ errorMessage }}</p>

      <!-- Until the sign-in options arrive the password form is the placeholder,
           since every install without SSO shows it. -->
      <AsyncBoundary skeleton="content" :loading="isCheckingSso" :empty="isCheckingSso">
        <!-- With SAML on, the local form folds away behind a trigger. Without it
           the form is always open and there is no trigger. display: contents
           keeps the column's gap between what this wraps. -->
        <Collapsible
          v-slot="{ open }"
          :open="!useSaml || showForm"
          class="contents"
          @update:open="(value) => (showForm = value)"
        >
          <!-- The form still renders below: local login may work without the provider list. -->
          <div
            v-if="ssoCheckFailed"
            data-testid="LoginCheckFailed"
            class="flex flex-col items-center gap-3 rounded-md border border-border bg-muted/40 p-4 text-sm text-muted-foreground"
          >
            <p class="text-center">{{ t("msg.loginCheckFailed") }}</p>
            <Button variant="outline" size="sm" @click="retrySsoCheck">{{ t("msg.tryNow") }}</Button>
          </div>

          <div
            v-else-if="noLoginMethod"
            data-testid="NoLoginMethod"
            class="rounded-md border border-border bg-muted/40 p-4 text-sm text-muted-foreground"
          >
            {{ t("msg.noLoginMethod") }}
          </div>

          <template v-else-if="samlOnlyMode">
            <!-- Web auto-fires a single IdP on mount and only renders buttons as a
             post-error retry. Native always renders them, and so does web when
             there are multiple IdPs to choose between: auto-firing would open a
             Custom Tab on every app launch / can't pick between IdPs. -->
            <template v-if="isNative || errorMessage || idps.length > 1">
              <button
                v-for="(idp, index) in idps"
                :key="idp.ID"
                :id="index === 0 ? 'login-sso-button' : undefined"
                :data-testid="`login-idp-${idp.ID || 'primary'}`"
                @click="samlRedirect(idp)"
                :class="
                  cn(
                    'inline-flex h-10 items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium whitespace-nowrap text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0',
                  )
                "
              >
                {{ samlLabel(idp) }}
              </button>
            </template>
          </template>

          <template v-else-if="useSaml">
            <button
              v-for="(idp, index) in idps"
              :key="idp.ID"
              :id="index === 0 ? 'login-sso-button' : undefined"
              :data-testid="`login-idp-${idp.ID || 'primary'}`"
              @click="samlRedirect(idp)"
              :class="
                cn(
                  'inline-flex h-10 items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium whitespace-nowrap text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0',
                )
              "
            >
              {{ samlLabel(idp) }}
            </button>

            <div class="relative">
              <div class="absolute inset-0 flex items-center" aria-hidden="true">
                <div class="w-full border-b border-foreground" />
              </div>
              <div class="relative flex justify-center text-sm leading-6 font-medium">
                <span class="bg-background px-6">
                  {{ t("msg.or") }}
                </span>
              </div>
            </div>

            <CollapsibleTrigger as-child>
              <Button type="button" variant="ghost" size="lg">
                {{ $t("msg.localLogin") }}
                <ChevronDownIcon class="transition-transform" :class="{ 'rotate-180': open }" aria-hidden="true" />
              </Button>
            </CollapsibleTrigger>
          </template>

          <CollapsibleContent class="contents">
            <KitForm v-if="!samlOnlyMode && !noLoginMethod" :schema="loginSchema" class="gap-4" @submit="handleLogin">
              <TextField
                name="username"
                :label="t('msg.username')"
                placeholder="john.doe"
                autocomplete="username"
                required
              />
              <TextField
                name="password"
                type="password"
                :label="t('msg.password')"
                placeholder="••••••••••"
                autocomplete="off"
                required
              />

              <SubmitButton :busy="isSubmitting" :disabled="isRateLimited" class="mt-6 w-full">
                {{ t("msg.login") }}
              </SubmitButton>

              <div v-if="!isNative" class="text-right text-sm">
                <a href="/admin" class="link">{{ t("msg.adminLoginIsHere") }}</a>
              </div>
            </KitForm>
          </CollapsibleContent>
        </Collapsible>
      </AsyncBoundary>

      <!-- Outside the login form: it says how the session is kept, and the
           SAML buttons above honour it too. It applies to every sign-in option,
           so it stays live while they load. -->
      <div v-if="!samlOnlyMode && !noLoginMethod && !isNative" class="mt-4 flex items-center justify-center gap-2">
        <Checkbox id="kioskMode" v-model="kioskMode" name="kioskMode" />
        <Label for="kioskMode" class="font-normal">This is a public computer</Label>
      </div>

      <div
        v-if="isNative && biometricReady && !isCheckingSso && !samlOnlyMode && !noLoginMethod"
        class="mt-4 flex justify-center"
      >
        <button
          @click="handleBiometricLogin"
          :disabled="isSubmitting"
          class="inline-flex items-center gap-2 rounded-md border border-border bg-background px-5 py-2.5 text-sm font-medium text-foreground shadow-xs transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50"
        >
          <Spinner v-if="isSubmitting" />
          <FingerprintIcon v-else class="size-4 text-muted-foreground" aria-hidden="true" />
          <span class="truncate">
            {{ biometricUsername ? t("msg.biometricSignInAs", { name: biometricUsername }) : t("msg.biometricSignIn") }}
          </span>
        </button>
      </div>
    </div>

    <!-- isNative is fixed for the page's life, so this gate never unmounts an open
         dialog. It keeps the dialog code out of the web bundle. -->
    <template v-if="isNative">
      <Dialog required :show="showBiometricSavePrompt">
        <DialogOverlay />

        <DialogPanel class="max-w-[400px]!">
          <DialogTitle>
            {{ biometricUpdateFromUser ? t("msg.biometricUpdateTitle") : t("msg.biometricSaveTitle") }}
          </DialogTitle>

          <p class="text-sm text-muted-foreground">
            {{
              biometricUpdateFromUser
                ? t("msg.biometricUpdateDescription", {
                    oldUser: biometricUpdateFromUser,
                    newUser: pendingBiometricCredentials?.username,
                  })
                : t("msg.biometricSaveDescription")
            }}
          </p>

          <div class="flex gap-3">
            <Button type="button" variant="outline" class="w-full" @click="handleBiometricSaveDecline">
              {{ t("msg.biometricSaveDecline") }}
            </Button>

            <button
              type="button"
              @click="handleBiometricSaveConfirm"
              class="inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground shadow-xs hover:bg-primary/90"
            >
              {{ t("msg.biometricSaveConfirm") }}
            </button>
          </div>
        </DialogPanel>
      </Dialog>
    </template>
  </div>
</template>

<script setup>
import { Spinner } from "@/components/ui/spinner";
import { until } from "@vueuse/core";
import { ChevronDownIcon, FingerprintIcon } from "lucide-vue-next";
import { computed, defineAsyncComponent, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { z } from "zod";

import AsyncBoundary from "@/components/kit/arrange/AsyncBoundary.vue";
import { KitForm, SubmitButton, TextField } from "@/components/kit/form";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Label } from "@/components/ui/label";
import { text } from "@/utils/formRules";
import { cn } from "@/lib/utils";

import globals from "../globals";
import { useLoginIdps } from "../queries/auth";
import { probeSystemStatus, usePublicSystemStatus } from "../queries/portal";
import { isApiError, rateLimitWaitSeconds } from "../services/apiError";

import WordmarkLogo from "../components/WordmarkLogo.vue";
import RateLimitCountdown from "../components/RateLimitCountdown.vue";
import { useAdminSession } from "../queries/session";
import { useUserSession } from "../queries/session";
import { REFERRER_QUERY_KEY, useRedirectUrl } from "../utils/useRedirectUrl";
import { resolveLoginIdps } from "../utils/loginIdpFallback";
import { setSamlAuthPending } from "../capacitor/auth-callback";
import { isNativePlatform } from "../capacitor/platform";
import { getServerUrl } from "../capacitor/server-url";
import {
  isBiometricAvailable,
  hasStoredCredentials,
  getStoredUsername,
  getCredentials,
  saveCredentials,
  deleteCredentials,
} from "../capacitor/biometric";
import { postLoginNavigate } from "../utils/postLoginNavigate";

// Loaded only when the native-only biometric prompt renders.
const loadDialog = () => import("@/components/kit/dialog");
const Dialog = defineAsyncComponent(() => loadDialog().then((m) => m.Dialog));
const DialogOverlay = defineAsyncComponent(() => loadDialog().then((m) => m.DialogOverlay));
const DialogPanel = defineAsyncComponent(() => loadDialog().then((m) => m.DialogPanel));
const DialogTitle = defineAsyncComponent(() => loadDialog().then((m) => m.DialogTitle));

const { t } = useI18n();

const adminSession = useAdminSession();
const userSession = useUserSession();
const router = useRouter();
const route = useRoute();

const loginSchema = z.object({ username: text({ required: true }), password: text({ required: true }) });

const errorMessage = ref("");
const rateLimitSeconds = ref(0);
// Public, so a signed-out visitor can read it. A failed read leaves local login on.
const publicStatus = usePublicSystemStatus();
const disableLocalUserLogin = computed(() => Boolean(publicStatus.data.value?.DisableLocalUserLogin));
// Available user-login IdPs (primary + additional). An empty ID is the primary.
// A failed read stays null, so it does not hide the SAML button.
const loginIdps = useLoginIdps();
const idps = computed(() => resolveLoginIdps(loginIdps.data.value ?? null, disableLocalUserLogin.value));
const useSaml = computed(() => idps.value.length > 0);
const isCheckingSso = computed(() => loginIdps.isPending.value || publicStatus.isPending.value);
// Both reads fail open, so this is the only sign the server was unreachable.
const ssoCheckFailed = computed(() => !isCheckingSso.value && (loginIdps.isError.value || publicStatus.isError.value));

function retrySsoCheck() {
  void loginIdps.refetch();
  void publicStatus.refetch();
}

const showForm = ref(false);
const kioskMode = ref(false);
const isRateLimited = computed(() => rateLimitSeconds.value > 0);
const isSubmitting = ref(false);
const isNative = isNativePlatform();
// Hides the local form and biometric (biometric posts to /api/login,
// which the server rejects when DisableLocalUserLogin is on). The
// "Change server" link sits outside this block so native can still
// switch backends.
const samlOnlyMode = computed(() => disableLocalUserLogin.value && useSaml.value);

// Local login is off and no IdP built, so nothing here can succeed. Without this
// every correct password 401s, which reads as a broken account.
const noLoginMethod = computed(() => disableLocalUserLogin.value && !useSaml.value);
const biometricReady = ref(false);
const biometricUsername = ref("");
const showBiometricSavePrompt = ref(false);
const pendingBiometricCredentials = ref(null);
const biometricUpdateFromUser = ref(null);
const bundleId = ref("");
const logoDiv = ref(null);
const isKeyboardOpen = ref(false);

const { getReferrerUrl } = useRedirectUrl([]);

watch(showForm, (visible) => {
  if (visible) {
    setTimeout(() => {
      document.querySelector('input[name="username"]')?.focus();
    }, 50);
  }
});

// Each IdP uses its display name (the primary has an empty ID). An install that
// never named the primary keeps the generic single sign-on label.
const samlLabel = (idp) => idp?.DisplayName || t("msg.sso");

const samlRedirect = async (idp) => {
  const referrerUrl = getReferrerUrl();
  let url = "api/login/saml?kioskMode=" + encodeURIComponent(String(kioskMode.value));

  if (idp && idp.ID) {
    url += "&idp=" + encodeURIComponent(idp.ID);
  }

  if (referrerUrl) {
    url += "&" + REFERRER_QUERY_KEY + "=" + encodeURIComponent(referrerUrl);
  }

  if (isNative) {
    const serverUrl = await getServerUrl();
    if (!serverUrl) return;

    // Check if the server supports mobile SAML (deep link callback flow).
    // Older servers redirect to /home in the browser instead.
    try {
      const status = await probeSystemStatus();
      if (!status?.MobileSaml) {
        errorMessage.value = t("msg.serverUpgradeRequiredForSso");
        return;
      }
    } catch {
      errorMessage.value = t("msg.error");
      return;
    }

    url = `${serverUrl}/${url}&mobile=true`;
    setSamlAuthPending();
    const { Browser } = await import("@capacitor/browser");
    await Browser.open({ url });
    // The deep link handler in auth-callback.ts completes the flow
    return;
  }

  location.href = url;
};

function triggerButtonClickIfUnfocused() {
  const active = document.activeElement;
  const skipTags = ["INPUT", "TEXTAREA", "BUTTON", "SELECT"];

  const isInteractive = active && (skipTags.includes(active.tagName) || active.isContentEditable);

  if (!isInteractive) {
    const loginButton = document.getElementById("login-sso-button");
    if (loginButton) loginButton.click();
  }
}

function onKeyDown(e) {
  // Only auto-trigger on Enter with exactly one IdP. With several, Enter must not
  // silently pick whichever button renders first.
  if (e.key === "Enter" && idps.value.length === 1 && !isCheckingSso.value) {
    triggerButtonClickIfUnfocused();
  }
}

async function handleBiometricLogin() {
  if (!isNative) return;
  const serverUrl = await getServerUrl();
  if (!serverUrl) return;

  isSubmitting.value = true;
  errorMessage.value = "";

  let creds = null;
  try {
    creds = await getCredentials(serverUrl);
  } catch {
    isSubmitting.value = false;
    return;
  }

  if (!creds) {
    isSubmitting.value = false;
    return;
  }

  try {
    const credentials = {
      UserName: creds.username,
      Password: creds.password,
      KioskMode: kioskMode.value,
    };
    await userSession.login(JSON.stringify(credentials));
    await userSession.fetchStatus({ force: true });
    postLoginNavigate(route);
  } catch (error) {
    const status = isApiError(error) ? error.status : undefined;
    if (status === 401 || status === 403) {
      await deleteCredentials(serverUrl);
      biometricReady.value = false;
      errorMessage.value = t("msg.biometricCredentialsExpired");
    } else {
      errorMessage.value = globals.renderError(t, error);
    }
  } finally {
    isSubmitting.value = false;
  }
}

async function handleLogin(fields) {
  if (rateLimitSeconds.value > 0) {
    return;
  }

  isSubmitting.value = true;

  const credentials = {
    UserName: fields.username,
    Password: fields.password,
    KioskMode: kioskMode.value,
  };

  try {
    await userSession.login(JSON.stringify(credentials));
    await userSession.fetchStatus({ force: true });

    // Offer to save or update credentials with biometrics on native
    if (isNative) {
      const available = await isBiometricAvailable();
      if (available) {
        const serverUrl = await getServerUrl();
        if (serverUrl) {
          const storedUser = await getStoredUsername(serverUrl);
          const isDifferentUser = storedUser && storedUser !== fields.username;
          const isNewSetup = !storedUser;

          if (isNewSetup || isDifferentUser) {
            pendingBiometricCredentials.value = {
              serverUrl,
              username: fields.username,
              password: fields.password,
            };
            biometricUpdateFromUser.value = isDifferentUser ? storedUser : null;
            showBiometricSavePrompt.value = true;
            return;
          }
        }
      }
    }

    postLoginNavigate(route);
  } catch (error) {
    const wait = rateLimitWaitSeconds(error);
    if (wait !== null) {
      rateLimitSeconds.value = wait;
      errorMessage.value = "";
      return;
    }
    if (isApiError(error) && error.kind === "http") {
      switch (error.code) {
        case "err.totpVerificationRequired":
          router.push({ name: "TwoFactor", query: route.query });
          break;

        case "err.totpEnrolmentRequired":
          router.push({ name: "TwoFactorOnboard", query: route.query });
          break;

        case "err.passwordChangeRequired":
          router.push({ name: "PasswordChange", query: route.query });
          break;

        case "err.unauthorised":
          if (isNative) {
            errorMessage.value = t("msg.adminLoginNotSupported");
            break;
          }
          await adminSession.fetchStatus({ force: true });
          if (adminSession.user.TotpEnabled) {
            if (adminSession.user.AdminTotpVerified) {
              window.location.href = "/admin/otp/verify";
            } else {
              window.location.href = "/admin/otp/onboard";
            }
          } else {
            window.location.href = "/admin/home";
          }
          break;
      }

      errorMessage.value = globals.renderError(t, error);
    }
  } finally {
    isSubmitting.value = false;
  }
}

async function handleBiometricSaveConfirm() {
  const creds = pendingBiometricCredentials.value;
  if (creds) {
    await saveCredentials(creds.serverUrl, creds.username, creds.password);
  }
  showBiometricSavePrompt.value = false;
  pendingBiometricCredentials.value = null;
  biometricUpdateFromUser.value = null;
  postLoginNavigate(route);
}

async function handleBiometricSaveDecline() {
  showBiometricSavePrompt.value = false;
  pendingBiometricCredentials.value = null;
  biometricUpdateFromUser.value = null;
  postLoginNavigate(route);
}

async function checkServerAndLogin() {
  // Run auth check, SSO check, and biometric check in parallel
  // so the login form appears as fast as possible.
  const authCheck = userSession.fetchStatus({ force: true }).catch(() => null);
  const ssoCheck = until(isCheckingSso).toBe(false);
  const serverUrl = isNative ? await getServerUrl() : null;
  const bioCheck =
    isNative && serverUrl
      ? Promise.all([isBiometricAvailable(), hasStoredCredentials(serverUrl), getStoredUsername(serverUrl)])
      : Promise.resolve(null);

  const [user, , bioResult] = await Promise.all([authCheck, ssoCheck, bioCheck]);

  if (user) {
    postLoginNavigate(route);
    return;
  }

  if (bioResult) {
    const [available, stored, username] = bioResult;
    biometricReady.value = available && stored;
    biometricUsername.value = username ?? "";
  }

  // Native waits for an explicit tap, or a Custom Tab opens on every launch. The error
  // guard stops a failing IdP looping, and several IdPs mean the user must choose.
  if (samlOnlyMode.value && idps.value.length === 1 && !isNative && !route.query.error) {
    if (route.query.sessionExpired) {
      router.replace({ name: "LoggedOut" });
      return;
    }
    samlRedirect(idps.value[0]);
  }
}

let viewportInitialHeight = 0;

function handleViewportResize() {
  if (window.visualViewport) {
    isKeyboardOpen.value = window.visualViewport.height < viewportInitialHeight * 0.75;
  }
}

// On native, @capacitor/keyboard fires precise lifecycle events. Returns a
// disposer that the unmount path calls. The viewport-resize heuristic is
// the web fallback only. It misfires on iPad split-view where the keyboard
// takes well under 25% of the screen height.
let keyboardListenerDispose = null;
async function installNativeKeyboardListeners() {
  const { Keyboard } = await import("@capacitor/keyboard");
  const showHandle = await Keyboard.addListener("keyboardWillShow", () => {
    isKeyboardOpen.value = true;
  });
  const hideHandle = await Keyboard.addListener("keyboardWillHide", () => {
    isKeyboardOpen.value = false;
  });
  keyboardListenerDispose = () => {
    showHandle.remove();
    hideHandle.remove();
  };
}

// React to route.query.error changes, not just the initial mount. SAML
// errors on native arrive via the knocknoc://auth-callback deep link, which
// pushes to the Login route with ?error=<key>. When the user is already on
// Login (the common case), router.push does not remount the component, so
// onMounted's one-shot check would miss the error.
watch(
  () => route.query.error,
  (key) => {
    if (typeof key === "string" && key) {
      errorMessage.value = t(key);
    }
  },
  { immediate: true },
);

onMounted(async () => {
  logoDiv.value?.focus();

  if (route.query.error) {
    errorMessage.value = t(route.query.error);
  }

  window.addEventListener("keydown", onKeyDown);

  if (isNative) {
    installNativeKeyboardListeners();
  } else if (window.visualViewport) {
    viewportInitialHeight = window.visualViewport.height;
    window.visualViewport.addEventListener("resize", handleViewportResize);
  }

  if (isNative) {
    try {
      const { default: AppUpdater } = await import("../capacitor/app-updater-plugin");
      const current = await AppUpdater.current();
      bundleId.value = current.id;
    } catch {
      // Plugin may not be available; leave version hidden.
    }
  }

  // On native, the router guard guarantees a server URL exists by the time we get here
  await checkServerAndLogin();
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", onKeyDown);
  if (window.visualViewport) {
    window.visualViewport.removeEventListener("resize", handleViewportResize);
  }
  keyboardListenerDispose?.();
  keyboardListenerDispose = null;
});
</script>

<style scoped>
@reference "../style.css";

.no-focus-outline:focus {
  outline: none;
}
</style>
